// Create new component: src/components/ImageUpload.js

import React, { useState, useRef } from 'react';
import { Camera, Upload, X, RotateCcw, Check, AlertCircle, Trash2 } from 'lucide-react';
import { imageService } from '../services/apiService';
import { 
  validateImageFile, 
  generateUniqueFileName, 
  compressImage, 
  createImagePreview, 
  cleanupImagePreview,
  formatFileSize,
  hasCameraSupport
} from '../utils/imageUtils';
import { IMAGE_CONSTRAINTS, IMAGE_TYPES } from '../constants';

const ImageUpload = ({ 
  shipmentId, 
  userData, 
  onUploadComplete, 
  onUploadError,
  maxFiles = 10,
  imageType = IMAGE_TYPES.SHIPMENT,
  disabled = false,
  trangthai = null
}) => {
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [overallProgress, setOverallProgress] = useState(0);
  const [deletingImageId, setDeletingImageId] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileSelection = async (files) => {
    const fileArray = Array.from(files);
    
    // Check file limit
    if (images.length + fileArray.length > maxFiles) {
      onUploadError?.(`Chỉ được phép upload tối đa ${maxFiles} ảnh`);
      return;
    }

    const newImages = [];
    
    for (const file of fileArray) {
      // Validate file
      const validation = validateImageFile(file);
      if (!validation.isValid) {
        onUploadError?.(validation.error);
        continue;
      }

      // Create image object
      const imageObj = {
        id: `img_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        file,
        name: file.name,
        size: file.size,
        preview: createImagePreview(file),
        status: 'pending', // pending, uploading, uploaded, failed
        progress: 0,
        error: null,
        serverId: null
      };

      newImages.push(imageObj);
    }

    setImages(prev => [...prev, ...newImages]);
  };

  const handleFileInput = (e) => {
    if (e.target.files.length > 0) {
      handleFileSelection(e.target.files);
    }
  };

  const removeImage = (imageId) => {
    setImages(prev => {
      const imageToRemove = prev.find(img => img.id === imageId);
      if (imageToRemove?.preview) {
        cleanupImagePreview(imageToRemove.preview);
      }
      return prev.filter(img => img.id !== imageId);
    });
  };

  const deleteImageFromServer = async (imageId, filePath) => {
    if (!filePath) {
      onUploadError?.('Không thể xóa ảnh: Đường dẫn không hợp lệ');
      return false;
    }

    setDeletingImageId(imageId);

    try {
      const result = await imageService.deleteImage(filePath);
      
      if (result.success) {
        // Remove from local state
        removeImage(imageId);
        return true;
      } else {
        onUploadError?.(result.message || 'Không thể xóa ảnh');
        return false;
      }
    } catch (error) {
      onUploadError?.(`Lỗi khi xóa ảnh: ${error.message}`);
      return false;
    } finally {
      setDeletingImageId(null);
    }
  };

  const uploadSingleImage = async (imageObj) => {
    try {
      // Update status to uploading
      setImages(prev => prev.map(img => 
        img.id === imageObj.id 
          ? { ...img, status: 'uploading', progress: 0 }
          : img
      ));

      // Compress image
      const compressedBase64 = await compressImage(imageObj.file);
      
      // Update progress
      setImages(prev => prev.map(img => 
        img.id === imageObj.id 
          ? { ...img, progress: 50 }
          : img
      ));

      // Generate unique filename
      const fileName = generateUniqueFileName(
        imageObj.name, 
        'avatar',
        shipmentId
      );
      console.log('shipmentId ',shipmentId);
      // Upload to server with status if provided
      const uploadResult = await imageService.uploadImage(
        userData?.UserName || userData?.userName || '',
        compressedBase64,
        fileName,
        imageType,
        shipmentId,
        trangthai
      );
      console.log("uploadResult ",uploadResult,uploadResult.data[0]?.ImageId );
//       0
// : 
// {ImageId: '24'}
// 1
// : 
// {ImageUrl: '~/Uploads/Images/20250618100436_55109b62.png'}
// 2
// : 
// {FileName: '20250618100436_55109b62.png'}
// uploadResult[0] = uploadResult[0] || {};
//       uploadResult[1] = uploadResult[1] || {};
//       uploadResult[2] = uploadResult[2] || {};
      if (uploadResult.success) {
        // Update status to uploaded
        setImages(prev => prev.map(img => 
          img.id === imageObj.id 
            ? { 
                ...img, 
                status: 'uploaded', 
                progress: 100, 
                serverId: uploadResult.data[0]?.ImageId || null,
                filePath: uploadResult.data[1]?.ImageUrl || null,
              }
            : img
        ));

        return { 
          success: true, 
          imageId: uploadResult.data[0]?.ImageId,
          filePath: uploadResult.data[1]?.ImageUrl
        };
      } else {
        throw new Error(uploadResult.message || 'Upload failed');
      }
    } catch (error) {
      // Update status to failed
      setImages(prev => prev.map(img => 
        img.id === imageObj.id 
          ? { ...img, status: 'failed', progress: 0, error: error.message }
          : img
      ));

      return { success: false, error: error.message };
    }
  };

  const uploadAllImages = async () => {
    const pendingImages = images.filter(img => img.status === 'pending');
    
    if (pendingImages.length === 0) {
      onUploadError?.('Không có ảnh nào để upload');
      return;
    }

    setUploading(true);
    setOverallProgress(0);

    const uploadResults = [];
    
    try {
      for (let i = 0; i < pendingImages.length; i++) {
        const result = await uploadSingleImage(pendingImages[i]);
        uploadResults.push(result);
        
        // Update overall progress
        const progress = ((i + 1) / pendingImages.length) * 100;
        setOverallProgress(progress);
      }

      const successfulUploads = uploadResults.filter(r => r.success);
      const failedUploads = uploadResults.filter(r => !r.success);
      console.log('successfulUploads ',successfulUploads);
      console.log('failedUploads ',failedUploads);
      if (successfulUploads.length > 0) {
        onUploadComplete?.({
          successful: successfulUploads.length,
          failed: failedUploads.length,
          total: uploadResults.length,
          imageIds: successfulUploads.map(r => r.imageId).filter(Boolean),
          images: successfulUploads.map(r => ({
            imageId: r.imageId,
            filePath: r.filePath
          })).filter(img => img.imageId && img.filePath)
        });
      }

      if (failedUploads.length > 0) {
        onUploadError?.(`${failedUploads.length} ảnh upload thất bại`);
      }

    } catch (error) {
      console.error('Upload process error:', error);
      onUploadError?.('Có lỗi xảy ra trong quá trình upload');
    } finally {
      setUploading(false);
      setOverallProgress(0);
    }
  };

  const retryFailedUploads = async () => {
    const failedImages = images.filter(img => img.status === 'failed');
    
    if (failedImages.length === 0) return;

    // Reset failed images to pending
    setImages(prev => prev.map(img => 
      img.status === 'failed' 
        ? { ...img, status: 'pending', error: null }
        : img
    ));

    // Upload them
    await uploadAllImages();
  };

  const clearAllImages = () => {
    // Clean up preview URLs
    images.forEach(img => {
      if (img.preview) {
        cleanupImagePreview(img.preview);
      }
    });
    
    setImages([]);
    setOverallProgress(0);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'uploading':
        return (
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
        );
      case 'uploaded':
        return <Check className="w-4 h-4 text-green-600" />;
      case 'failed':
        return <AlertCircle className="w-4 h-4 text-red-600" />;
      default:
        return null;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'uploading':
        return 'border-blue-500 bg-blue-50';
      case 'uploaded':
        return 'border-green-500 bg-green-50';
      case 'failed':
        return 'border-red-500 bg-red-50';
      default:
        return 'border-gray-300 bg-white';
    }
  };

  const pendingCount = images.filter(img => img.status === 'pending').length;
  const uploadedCount = images.filter(img => img.status === 'uploaded').length;
  const failedCount = images.filter(img => img.status === 'failed').length;

  return (
    <div className="space-y-4">
      {/* Upload Area */}
      <div className="space-y-3">
        <label 
          className={`flex items-center justify-center w-full p-6 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${
            disabled 
              ? 'border-gray-200 bg-gray-50 cursor-not-allowed' 
              : 'border-gray-300 hover:bg-gray-50'
          }`}
        >
          <Camera className="w-8 h-8 mr-3 text-gray-400" />
          <div className="text-center">
            <span className="text-lg font-medium">
              {disabled ? 'Upload không khả dụng' : 'Chọn ảnh hoặc chụp ảnh'}
            </span>
            <p className="text-sm text-gray-500 mt-1">
              Hỗ trợ nhiều ảnh cùng lúc (tối đa {formatFileSize(IMAGE_CONSTRAINTS.MAX_FILE_SIZE)}/ảnh)
            </p>
            {hasCameraSupport() && (
              <p className="text-xs text-blue-600 mt-1">
                Sử dụng camera để chụp trực tiếp
              </p>
            )}
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            onChange={handleFileInput}
            className="hidden"
            disabled={disabled || uploading}
          />
        </label>

        {/* Action Buttons */}
        {images.length > 0 && (
          <div className="flex gap-3">
            {pendingCount > 0 && (
              <button
                onClick={uploadAllImages}
                disabled={uploading || disabled}
                className="flex-1 bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors font-medium flex items-center justify-center gap-2"
              >
                <Upload className="w-4 h-4" />
                {uploading ? 'Đang upload...' : `Upload ${pendingCount} ảnh`}
              </button>
            )}

            {failedCount > 0 && (
              <button
                onClick={retryFailedUploads}
                disabled={uploading || disabled}
                className="px-4 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:bg-gray-400 transition-colors flex items-center gap-2"
              >
                <RotateCcw className="w-4 h-4" />
                Thử lại ({failedCount})
              </button>
            )}

            <button
              onClick={clearAllImages}
              disabled={uploading || disabled}
              className="px-4 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 disabled:bg-gray-400 transition-colors flex items-center gap-2"
            >
              <X className="w-4 h-4" />
              Xóa tất cả
            </button>
          </div>
        )}
      </div>

      {/* Overall Progress */}
      {uploading && overallProgress > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-blue-700">
              Tiến trình upload
            </span>
            <span className="text-sm text-blue-600">
              {Math.round(overallProgress)}%
            </span>
          </div>
          <div className="w-full bg-blue-200 rounded-full h-2">
            <div 
              className="bg-blue-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${overallProgress}%` }}
            ></div>
          </div>
        </div>
      )}

      {/* Images Grid */}
      {images.length > 0 && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="font-medium">
              Ảnh đã chọn ({images.length})
            </h4>
            
            {/* Status Summary */}
            <div className="flex items-center gap-4 text-sm">
              {uploadedCount > 0 && (
                <span className="text-green-600">✓ {uploadedCount} đã upload</span>
              )}
              {pendingCount > 0 && (
                <span className="text-gray-600">⏳ {pendingCount} chờ upload</span>
              )}
              {failedCount > 0 && (
                <span className="text-red-600">❌ {failedCount} lỗi</span>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {images.map((img) => (
              <div 
                key={img.id} 
                className={`relative border-2 rounded-lg overflow-hidden ${getStatusColor(img.status)}`}
              >
                {/* Image Preview */}
                <div className="relative">
                  <img
                    src={img.preview}
                    alt={img.name}
                    className="w-full h-32 object-cover"
                  />
                  
                  {/* Status Overlay */}
                  <div className="absolute top-2 left-2 flex items-center gap-1">
                    {getStatusIcon(img.status)}
                  </div>

                  {/* Progress Bar for Uploading */}
                  {img.status === 'uploading' && (
                    <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 p-1">
                      <div className="w-full bg-gray-300 rounded-full h-1">
                        <div 
                          className="bg-blue-600 h-1 rounded-full transition-all duration-300"
                          style={{ width: `${img.progress}%` }}
                        ></div>
                      </div>
                    </div>
                  )}

                  {/* Action Buttons */}
                  {img.status !== 'uploading' && (
                    <div className="absolute top-2 right-2 flex gap-1">
                      {/* Delete from server button (only for uploaded images) */}
                      {img.status === 'uploaded' && img.serverId && (
                        <button
                          onClick={() => deleteImageFromServer(img.id, img.filePath)}
                          className="bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm hover:bg-red-600 shadow-lg"
                          disabled={uploading || deletingImageId === img.id}
                          title="Xóa ảnh khỏi máy chủ"
                        >
                          {deletingImageId === img.id ? (
                            <div className="animate-spin h-3 w-3 border-2 border-white rounded-full border-t-transparent"></div>
                          ) : (
                            <Trash2 className="w-3 h-3" />
                          )}
                        </button>
                      )}
                      
                      {/* Remove from list button */}
                      <button
                        onClick={() => removeImage(img.id)}
                        className="bg-gray-700 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm hover:bg-gray-800 shadow-lg"
                        disabled={uploading || deletingImageId === img.id}
                        title="Xóa khỏi danh sách"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  )}
                </div>

                {/* Image Info */}
                <div className="p-2">
                  <p className="text-xs text-gray-600 truncate">{img.name}</p>
                  <p className="text-xs text-gray-500">{formatFileSize(img.size)}</p>
                  
                  {img.error && (
                    <p className="text-xs text-red-600 mt-1" title={img.error}>
                      ❌ {img.error.substring(0, 30)}...
                    </p>
                  )}
                  
                  {img.status === 'uploaded' && (
                    <p className="text-xs text-green-600 mt-1">✓ Đã upload</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Help Text */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
        <h5 className="text-sm font-medium text-gray-700 mb-2">Hướng dẫn:</h5>
        <ul className="text-xs text-gray-600 space-y-1">
          <li>• Chụp đơn hàng</li>
          <li>• Chụp ảnh hàng hóa tại điểm giao</li>
          <li>• Kích thước tối đa mỗi ảnh: {formatFileSize(IMAGE_CONSTRAINTS.MAX_FILE_SIZE)}</li>
          <li>• Ảnh sẽ được tự động nén để tối ưu</li>
          <li>• Hỗ trợ định dạng: JPG, PNG, WebP</li>
        </ul>
      </div>
    </div>
  );
};

export default ImageUpload;
